import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, X, Calculator, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

const emptyItem = () => ({
  descripcion: "", unidad: "Pieza", cantidad: "", precio_unitario: "",
});

function fmtMoney(v) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
}

export default function CotizacionesACNewPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    folio: "",
    cliente: "",
    contacto: "",
    fecha: new Date().toISOString().slice(0, 10),
    vigencia: "30 días",
    condiciones_pago: "",
    items: [emptyItem()],
    indicaciones: [""],
    notas: "",
  });

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  /* ── Items ── */
  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm(prev => ({ ...prev, items }));
  };
  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, emptyItem()] }));
  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  /* ── Indicaciones ── */
  const updateIndicacion = (idx, val) => {
    const indicaciones = [...form.indicaciones];
    indicaciones[idx] = val;
    setForm(prev => ({ ...prev, indicaciones }));
  };
  const addIndicacion = () => setForm(prev => ({ ...prev, indicaciones: [...prev.indicaciones, ""] }));
  const removeIndicacion = (idx) => {
    if (form.indicaciones.length <= 1) return;
    setForm(prev => ({ ...prev, indicaciones: prev.indicaciones.filter((_, i) => i !== idx) }));
  };

  /* ── Totals ── */
  const totals = useMemo(() => {
    let subtotal = 0;
    form.items.forEach(it => {
      const qty = parseFloat(it.cantidad) || 0;
      const price = parseFloat(it.precio_unitario) || 0;
      subtotal += qty * price;
    });
    subtotal = Math.round(subtotal * 100) / 100;
    const iva = Math.round(subtotal * 0.16 * 100) / 100;
    const total = Math.round((subtotal + iva) * 100) / 100;
    return { subtotal, iva, total };
  }, [form.items]);

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!form.cliente.trim()) {
      toast.error("El cliente es requerido");
      return;
    }
    const validItems = form.items.filter(it => it.descripcion.trim());
    if (validItems.length === 0) {
      toast.error("Agrega al menos un concepto con descripción");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: validItems.map(it => ({
          descripcion: it.descripcion,
          unidad: it.unidad || "Pieza",
          cantidad: parseFloat(it.cantidad) || 0,
          precio_unitario: parseFloat(it.precio_unitario) || 0,
        })),
        indicaciones: form.indicaciones.filter(i => i.trim()),
      };
      const res = await api.post("/cotizaciones-ac", payload);
      toast.success(`Cotización ${res.data.folio} creada`);
      navigate("/ots-comercial?tab=cotizador");
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al crear cotización";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-[1100px]" data-testid="cotizacion-new-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/ots-comercial?tab=cotizador" data-testid="cotizacion-back-button">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Nueva Cotización</h1>
          <p className="text-sm text-muted-foreground">Crea una cotización para el área comercial.</p>
        </div>
      </div>

      {/* General data */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Datos Generales</CardTitle>
          <CardDescription className="text-xs">Información del cliente y condiciones de la cotización.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Folio <span className="text-[10px]">(vacío = automático)</span></Label>
              <Input
                placeholder="Ej: COT-AC-0001"
                value={form.folio}
                onChange={(e) => setField("folio", e.target.value)}
                className="font-mono"
                data-testid="cotizacion-folio-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Cliente *</Label>
              <Input
                placeholder="Nombre del cliente"
                value={form.cliente}
                onChange={(e) => setField("cliente", e.target.value)}
                data-testid="cotizacion-cliente-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Contacto</Label>
              <Input
                placeholder="Nombre de contacto"
                value={form.contacto}
                onChange={(e) => setField("contacto", e.target.value)}
                data-testid="cotizacion-contacto-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Fecha</Label>
              <Input
                type="date"
                value={form.fecha}
                onChange={(e) => setField("fecha", e.target.value)}
                data-testid="cotizacion-fecha-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Vigencia</Label>
              <Input
                placeholder="Ej: 30 días"
                value={form.vigencia}
                onChange={(e) => setField("vigencia", e.target.value)}
                data-testid="cotizacion-vigencia-input"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Condiciones de Pago</Label>
              <Input
                placeholder="Ej: Crédito 30 días"
                value={form.condiciones_pago}
                onChange={(e) => setField("condiciones_pago", e.target.value)}
                data-testid="cotizacion-condiciones-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items / Conceptos */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm">Conceptos / Servicios</CardTitle>
              <CardDescription className="text-xs">Detalle de lo que incluye la cotización.</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="cotizacion-add-item">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[650px]">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 px-3 w-8">#</th>
                  <th className="py-2 px-3">Descripción *</th>
                  <th className="py-2 px-3 w-24">Unidad</th>
                  <th className="py-2 px-3 w-24">Cantidad</th>
                  <th className="py-2 px-3 w-32">Precio Unitario</th>
                  <th className="py-2 px-3 w-32 text-right">Importe</th>
                  <th className="py-2 px-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => {
                  const qty = parseFloat(it.cantidad) || 0;
                  const price = parseFloat(it.precio_unitario) || 0;
                  const importe = Math.round(qty * price * 100) / 100;
                  return (
                    <tr key={idx} className="border-t border-border/60" data-testid={`cotizacion-item-row-${idx}`}>
                      <td className="py-2 px-3 text-muted-foreground text-center">{idx + 1}</td>
                      <td className="py-2 px-3">
                        <Input
                          placeholder="Descripción del servicio"
                          value={it.descripcion}
                          onChange={(e) => updateItem(idx, "descripcion", e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`cotizacion-item-desc-${idx}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          placeholder="Pieza"
                          value={it.unidad}
                          onChange={(e) => updateItem(idx, "unidad", e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`cotizacion-item-unidad-${idx}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={it.cantidad}
                          onChange={(e) => updateItem(idx, "cantidad", e.target.value)}
                          className="h-8 text-sm font-mono tabular-nums"
                          data-testid={`cotizacion-item-qty-${idx}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={it.precio_unitario}
                          onChange={(e) => updateItem(idx, "precio_unitario", e.target.value)}
                          className="h-8 text-sm font-mono tabular-nums"
                          data-testid={`cotizacion-item-price-${idx}`}
                        />
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums font-medium">
                        {fmtMoney(importe)}
                      </td>
                      <td className="py-2 px-3">
                        {form.items.length > 1 && (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(idx)} data-testid={`cotizacion-item-remove-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Totals bar */}
          <div className="flex justify-end border-t border-border p-4">
            <div className="w-72 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-mono tabular-nums" data-testid="cotizacion-subtotal">{fmtMoney(totals.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">IVA (16%):</span>
                <span className="font-mono tabular-nums" data-testid="cotizacion-iva">{fmtMoney(totals.iva)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold border-t border-border pt-1.5">
                <span>Total:</span>
                <span className="font-mono tabular-nums text-primary" data-testid="cotizacion-total">{fmtMoney(totals.total)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Indicaciones / Incluye */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-sm">Indicaciones / Incluye</CardTitle>
              <CardDescription className="text-xs">Lista de lo que incluye esta cotización. Aparecerá numerada en el PDF.</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={addIndicacion} data-testid="cotizacion-add-indicacion">
              <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {form.indicaciones.map((ind, idx) => (
              <div key={idx} className="flex items-center gap-2" data-testid={`cotizacion-indicacion-${idx}`}>
                <Badge variant="secondary" className="shrink-0 text-[10px] w-6 h-6 justify-center p-0">{idx + 1}</Badge>
                <Input
                  placeholder={`Indicación ${idx + 1}`}
                  value={ind}
                  onChange={(e) => updateIndicacion(idx, e.target.value)}
                  className="h-8 text-sm"
                  data-testid={`cotizacion-indicacion-input-${idx}`}
                />
                {form.indicaciones.length > 1 && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0 text-destructive" onClick={() => removeIndicacion(idx)} data-testid={`cotizacion-indicacion-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Notas */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Notas</CardTitle>
          <CardDescription className="text-xs">Notas adicionales (aparecen en el PDF).</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Notas adicionales, condiciones especiales..."
            value={form.notas}
            onChange={(e) => setField("notas", e.target.value)}
            className="min-h-[80px]"
            data-testid="cotizacion-notas-input"
          />
        </CardContent>
      </Card>

      {/* Footer actions */}
      <div className="flex justify-end gap-3 pb-6">
        <Button variant="outline" asChild data-testid="cotizacion-cancel-button">
          <Link to="/ots-comercial?tab=cotizador">
            <X className="h-4 w-4 mr-1" /> Cancelar
          </Link>
        </Button>
        <Button onClick={handleSubmit} disabled={saving} data-testid="cotizacion-save-button">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Guardando..." : "Guardar Cotización"}
        </Button>
      </div>
    </div>
  );
}
